const EMPTY_VALUE = '—';

const numberFormatter = new Intl.NumberFormat('pt-BR');
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});
const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export const isRecord = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const valueAt = (object, path, fallback = null) => {
  const value = path.reduce((current, key) => current?.[key], object);
  return value === undefined || value === null ? fallback : value;
};

export const formatNumber = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? numberFormatter.format(numeric)
    : EMPTY_VALUE;
};

export const formatPercent = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? `${percentFormatter.format(numeric)}%`
    : EMPTY_VALUE;
};

export const formatCurrencyCents = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? currencyFormatter.format(numeric / 100)
    : EMPTY_VALUE;
};

export const formatMilliseconds = value => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return EMPTY_VALUE;
  if (numeric < 1000) return `${Math.round(numeric)}ms`;

  const totalSeconds = Math.round(numeric / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const operationStatusTone = status => {
  const normalized = String(status || '').toLowerCase();
  if (
    ['healthy', 'ok', 'quiet', 'passed', 'closed', 'ready'].includes(normalized)
  ) {
    return 'positive';
  }
  if (
    ['no_data', 'skipped', 'draft', 'manual_review_only'].includes(normalized)
  ) {
    return 'neutral';
  }
  if (
    [
      'critical',
      'danger',
      'error',
      'warning',
      'breached',
      'open',
      'firing',
      'unhealthy',
    ].includes(normalized)
  ) {
    return 'danger';
  }
  if (['degraded', 'stale', 'pending'].includes(normalized)) return 'warning';
  if (['running', 'info'].includes(normalized)) return 'info';
  return 'neutral';
};

export const availabilityLabel = (ready, t) =>
  ready
    ? t('CAPTAIN_EXPERIENCE.OPERATIONS.READY')
    : t('CAPTAIN_EXPERIENCE.OPERATIONS.NOT_READY');

export const buildOperationMetrics = (snapshot, t) => [
  {
    id: 'readiness',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_READINESS'),
    value: availabilityLabel(
      valueAt(snapshot, ['readiness', 'ready'], false),
      t
    ),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_READINESS_HELPER'),
    icon: 'i-lucide-shield-check',
    tone: valueAt(snapshot, ['readiness', 'ready'], false)
      ? 'positive'
      : 'warning',
  },
  {
    id: 'success_rate',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_SUCCESS_RATE'),
    value: formatPercent(
      valueAt(snapshot, ['metrics', 'runs', 'success_rate_percent'])
    ),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_SUCCESS_RATE_HELPER'),
    icon: 'i-lucide-circle-check-big',
    tone: operationStatusTone(
      valueAt(snapshot, ['slo', 'success_rate', 'status'])
    ),
  },
  {
    id: 'latency_p95',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_LATENCY_P95'),
    value: formatMilliseconds(
      valueAt(snapshot, ['metrics', 'latency', 'p95_ms'])
    ),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_LATENCY_P95_HELPER'),
    icon: 'i-lucide-timer',
    tone: operationStatusTone(
      valueAt(snapshot, ['slo', 'latency_p95', 'status'])
    ),
  },
  {
    id: 'groundedness',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_GROUNDEDNESS'),
    value: formatPercent(
      valueAt(snapshot, ['metrics', 'groundedness', 'groundedness_percent'])
    ),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_GROUNDEDNESS_HELPER'),
    icon: 'i-lucide-badge-check',
    tone: operationStatusTone(
      valueAt(snapshot, ['slo', 'groundedness', 'status'])
    ),
  },
  {
    id: 'cost',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_COST'),
    value: formatCurrencyCents(
      valueAt(snapshot, ['metrics', 'cost', 'total_cents'])
    ),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_COST_HELPER'),
    icon: 'i-lucide-coins',
    tone: 'neutral',
  },
  {
    id: 'dlq',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_DLQ'),
    value: formatNumber(valueAt(snapshot, ['dlq', 'retryable_count'], 0)),
    helper: t('CAPTAIN_EXPERIENCE.OPERATIONS.METRIC_DLQ_HELPER'),
    icon: 'i-lucide-inbox',
    tone: operationStatusTone(
      valueAt(snapshot, ['slo', 'retryable_dlq', 'status'])
    ),
  },
];

const buildSloLabelMap = t => ({
  success_rate: t('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_SUCCESS_RATE'),
  latency_p95: t('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_LATENCY_P95'),
  groundedness: t('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_GROUNDEDNESS'),
  tool_failures: t('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_TOOL_FAILURES'),
  retryable_dlq: t('CAPTAIN_EXPERIENCE.OPERATIONS.SLO_RETRYABLE_DLQ'),
});

const formatSloValue = key => value => {
  if (key === 'latency_p95') return formatMilliseconds(value);
  if (['success_rate', 'groundedness', 'tool_failures'].includes(key)) {
    return formatPercent(value);
  }
  return formatNumber(value);
};

export const buildSloItems = (slo, t) => {
  const labels = buildSloLabelMap(t);
  return Object.keys(labels).map(key => {
    const item = isRecord(slo?.[key]) ? slo[key] : {};
    const formatter = formatSloValue(key);
    return {
      key,
      label: labels[key],
      status: item.status || 'no_data',
      tone: operationStatusTone(item.status),
      value: formatter(item.value),
      target: formatter(item.target),
      direction: item.direction || '',
    };
  });
};

export const buildReadinessItems = (readiness, t) => [
  {
    key: 'captain_enabled',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.READINESS_CAPTAIN_ENABLED'),
    value: availabilityLabel(readiness?.captain_enabled, t),
    tone: readiness?.captain_enabled ? 'positive' : 'danger',
  },
  {
    key: 'advanced_runtime_enabled',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.READINESS_ADVANCED_RUNTIME'),
    value: availabilityLabel(readiness?.advanced_runtime_enabled, t),
    tone: readiness?.advanced_runtime_enabled ? 'positive' : 'danger',
  },
  {
    key: 'publication_mode',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.READINESS_PUBLICATION_MODE'),
    value: readiness?.publication_mode || EMPTY_VALUE,
    tone: operationStatusTone(readiness?.publication_mode),
  },
  {
    key: 'tool_catalog_count',
    label: t('CAPTAIN_EXPERIENCE.OPERATIONS.READINESS_TOOL_CATALOG'),
    value: formatNumber(readiness?.tool_catalog_count),
    tone: Number(readiness?.tool_catalog_count) > 0 ? 'positive' : 'warning',
  },
];

export const replayPlanTone = plan =>
  plan?.eligible ? 'positive' : operationStatusTone(plan?.retry_mode);

export const buildReplayPlanSummary = (plan, t) => {
  if (!isRecord(plan)) return [];

  return [
    {
      key: 'eligible',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_ELIGIBLE'),
      value: plan.eligible
        ? t('CAPTAIN_EXPERIENCE.OPERATIONS.YES')
        : t('CAPTAIN_EXPERIENCE.OPERATIONS.NO'),
    },
    {
      key: 'reason',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_REASON'),
      value: plan.reason || EMPTY_VALUE,
    },
    {
      key: 'mode',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_MODE'),
      value: plan.retry_mode || EMPTY_VALUE,
    },
    {
      key: 'risk',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_RISK'),
      value: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_RISK_VALUE', {
        count: formatNumber(plan.risk?.risky_tool_calls_count),
        recoverable: formatNumber(
          plan.risk?.recoverable_risky_tool_calls_count
        ),
      }),
    },
    {
      key: 'idempotency',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.REPLAY_IDEMPOTENCY'),
      value: plan.idempotency?.external_key_present
        ? t('CAPTAIN_EXPERIENCE.OPERATIONS.PRESENT')
        : t('CAPTAIN_EXPERIENCE.OPERATIONS.MISSING'),
    },
  ];
};

export const buildHandoffPlanSummary = (plan, t) => {
  if (!isRecord(plan)) return [];

  return [
    {
      key: 'recommended',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_RECOMMENDED'),
      value: plan.recommended
        ? t('CAPTAIN_EXPERIENCE.OPERATIONS.YES')
        : t('CAPTAIN_EXPERIENCE.OPERATIONS.NO'),
    },
    {
      key: 'reason',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_REASON'),
      value: plan.reason || EMPTY_VALUE,
    },
    {
      key: 'risks',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_RISKS'),
      value: formatNumber(Array.isArray(plan.risks) ? plan.risks.length : 0),
    },
    {
      key: 'tasks',
      label: t('CAPTAIN_EXPERIENCE.OPERATIONS.HANDOFF_TASKS'),
      value: formatNumber(
        Array.isArray(plan.suggested_tasks) ? plan.suggested_tasks.length : 0
      ),
    },
  ];
};

export const buildDlqItems = (snapshot, replayPlansByRunId = {}) => {
  const items = Array.isArray(snapshot?.dlq?.items) ? snapshot.dlq.items : [];
  return items.map(item => ({
    ...item,
    currentPlan: replayPlansByRunId[item.run_id] || item,
    tone: replayPlanTone(replayPlansByRunId[item.run_id] || item),
  }));
};

export const redactedViewerKeys = Object.freeze([
  'password',
  'token',
  'secret',
  'authorization',
  'cpf',
  'cnpj',
  'phone',
  'email',
  'document',
  'bank',
  'account',
  'pix',
  'payload',
  'arguments',
  'result',
  'message',
  'idempotency',
  'key',
  'sha256',
  'evidence',
  'record_ref',
  'summary',
]);
